export function resolveVariant(config, slug, finish) {
  const name = config.finishes[finish]?.name;
  const matches = config.products[slug]?.variants.filter(variant => variant.finish === name) || [];
  return matches.length === 1 ? matches[0] : null;
}

export function cartQuantities(items = []) {
  const quantities = new Map();
  for (const item of items) {
    const id = String(item.variant_id || item.id);
    quantities.set(id, (quantities.get(id) || 0) + Number(item.quantity || 0));
  }
  return quantities;
}

export function evaluateSelection(config, slots, inCart = new Map()) {
  const lines = slots.map(slot => ({ ...slot, variant: resolveVariant(config, slot.slug, slot.finish) }));
  const quantities = new Map();
  let issue = null;
  for (const [index, line] of lines.entries()) {
    const variant = line.variant;
    if (!line.slug) issue ||= { type: 'missing', index };
    else if (!variant?.id || !Number.isFinite(variant.price) || variant.price < 0 || !variant.available) issue ||= { type: 'unavailable', index };
    if (variant?.id) quantities.set(String(variant.id), (quantities.get(String(variant.id)) || 0) + 1);
  }
  for (const [index, line] of lines.entries()) {
    const variant = line.variant;
    if (variant?.inventoryManagement && variant.inventoryPolicy === 'deny') {
      const requested = (quantities.get(String(variant.id)) || 0) + (inCart.get(String(variant.id)) || 0);
      if (!Number.isFinite(variant.inventoryQuantity) || requested > variant.inventoryQuantity) issue ||= { type: 'stock', index };
    }
  }
  const gross = lines.every(line => Number.isFinite(line.variant?.price)) ? lines.reduce((sum, line) => sum + line.variant.price, 0) : null;
  return {
    lines, issue, valid: !issue,
    items: Array.from(quantities, ([id, quantity]) => ({ id, quantity })),
    gross, total: gross === null ? null : Math.round(gross * (slots.length === 3 ? 0.7 : slots.length === 2 ? 0.8 : 1))
  };
}

export function initFinishPurchase(profile) {
  if (profile.dataset.finishReady) return;
  const node = profile.querySelector('[data-nw-finish-config]');
  if (!node) return;
  const config = JSON.parse(node.textContent);
  const offers = profile.querySelector('[data-nw-offers]');
  const form = profile.querySelector('[data-nw-finish-form]');
  const button = profile.querySelector('[data-nw-finish-submit]');
  const status = profile.querySelector('[data-nw-finish-status]');
  const cartLink = profile.querySelector('[data-nw-bundle-cart]');
  const bundle = profile.querySelector('[data-nw-bundle-buy]');
  const guidance = profile.querySelector('[data-nw-circle-guidance]');
  if (!offers || !form || !button || !status || !cartLink || !bundle || !guidance) return;
  const choices = { 1: [], 2: ['', ''], 3: ['', '', ''] };
  const cardFor = size => offers.querySelector('[data-nw-finish-card="' + size + '"]');
  const pickerFor = slot => offers.querySelector('[data-nw-choice="' + slot + '"] nw-ally-picker');
  const count = () => Number(offers.querySelector('.nw-offer input[type="radio"]:checked')?.value || 1);
  const slotsFor = (size, estimate = false) => Array.from({ length: size }, (_, index) => ({
    slug: index === 0 ? config.current : choices[size][index] || (estimate ? config.current : ''),
    finish: cardFor(size).querySelector('[data-nw-finish-slot="' + (index + 1) + '"]').value
  }));
  const money = cents => cents === null ? '—' : new Intl.NumberFormat(document.documentElement.lang || 'en', { style: 'currency', currency: config.currency || 'USD' }).format(cents / 100);
  const finishName = finish => config.finishes[finish]?.label || '';
  const familiarName = (slug, slot) => slot === 1 ? config.currentName : Array.from(pickerFor(slot).querySelector('select').options).find(option => option.value === slug)?.dataset.name || (slot === 2 ? config.secondName : config.thirdName);
  const issueMessage = result => result.issue?.type === 'missing' ? (result.issue.index === 1 ? config.chooseSecond : config.chooseThird) : result.issue?.type === 'stock' ? config.stock : config.unavailable;
  let inCart = cartQuantities(config.cartItems);
  let busy = false, needsCartReview = false, message = '', heroSelection = null, activeOffer = count();

  function refresh() {
    const size = count();
    bundle.hidden = size === 1;
    guidance.hidden = size === 1;
    offers.querySelectorAll('[data-nw-choice]').forEach(choice => { choice.hidden = Number(choice.dataset.nwChoice) > size; });
    offers.querySelector('[data-nw-circle-instructions]').textContent = size === 2 ? config.pairGuide : config.trioGuide;
    for (let offer = 1; offer <= 3; offer++) {
      const card = cardFor(offer), slots = slotsFor(offer, true), estimate = evaluateSelection(config, slots);
      card.querySelector('.nw-offer-finishes').hidden = offer !== size;
      card.querySelector('[data-nw-finish-price]').textContent = money(estimate.total);
      const reference = card.querySelector('[data-nw-finish-reference]');
      if (reference) reference.textContent = money(estimate.gross);
      card.querySelectorAll('[data-nw-finish-name]').forEach(label => {
        const slot = Number(label.dataset.nwFinishName);
        label.textContent = familiarName(slot === 1 ? config.current : choices[offer][slot - 1], slot);
      });
      card.querySelectorAll('[data-nw-finish-slot]').forEach(select => {
        const slot = Number(select.dataset.nwFinishSlot), slug = slots[slot - 1].slug;
        for (const option of select.options) {
          const available = evaluateSelection(config, [{ slug, finish: option.value }], inCart).valid;
          option.textContent = finishName(option.value) + (available ? '' : ' · ' + config.unavailableOption);
        }
      });
    }
    const selected = slotsFor(size), result = evaluateSelection(config, selected, inCart);
    const principal = result.lines[0].variant;
    const nextHeroSelection = size + ':' + selected[0].finish + ':' + (principal?.id || '');
    if (nextHeroSelection !== heroSelection) {
      heroSelection = nextHeroSelection;
      profile.dataset.finishMediaId = principal?.mediaId || '';
      profile.dispatchEvent(new CustomEvent('nw:finish-media-change', { detail: { mediaId: principal?.mediaId || null } }));
    }
    const summary = profile.querySelector('[data-nw-bundle-summary]');
    summary.replaceChildren();
    selected.forEach((slot, index) => {
      const line = document.createElement('span');
      line.className = 'nw-circle-item';
      line.textContent = (index + 1) + '. ' + familiarName(slot.slug, index + 1) + ' · ' + finishName(slot.finish);
      summary.appendChild(line);
    });
    profile.querySelector('[data-nw-bundle-total]').textContent = selected.every(slot => slot.slug) ? money(result.total) : '';
    button.disabled = busy || needsCartReview || !result.valid;
    button.setAttribute('aria-busy', String(busy));
    const mediaMissing = principal && !principal.mediaId;
    status.textContent = message || (busy ? config.adding : !result.valid ? issueMessage(result) : mediaMissing ? config.photoUnavailable : '');
    const input = form.querySelector('[name="id"]');
    input.value = size === 1 && principal ? principal.id : '';
    input.disabled = size !== 1 || !result.valid;
  }

  offers.addEventListener('click', event => {
    if (busy) return;
    const card = event.target.closest('[data-nw-finish-card]');
    if (!card || event.target.closest('select,option,label,button,a,input')) return;
    const radio = card.querySelector('input[type="radio"]');
    if (!radio.checked) { radio.checked = true; radio.dispatchEvent(new Event('change', { bubbles: true })); }
  });
  offers.addEventListener('change', event => {
    if (busy) return;
    if (event.target.type === 'radio') {
      activeOffer = count();
      for (let slot = 2; slot <= 3; slot++) {
        const picker = pickerFor(slot);
        picker.querySelector('select').value = choices[activeOffer][slot - 1] || '';
        picker.sync?.();
        picker.close?.();
      }
    } else {
      const choice = event.target.closest('[data-nw-choice]');
      if (choice) choices[activeOffer][Number(choice.dataset.nwChoice) - 1] = event.target.value;
    }
    if (!needsCartReview) { message = ''; cartLink.hidden = true; }
    refresh();
  });

  form.addEventListener('submit', async event => {
    event.preventDefault();
    if (busy || needsCartReview) return;
    const slots = slotsFor(count());
    let selected = evaluateSelection(config, slots, inCart);
    if (!selected.valid) { message = issueMessage(selected); refresh(); return; }
    busy = true; offers.disabled = true; message = ''; cartLink.hidden = true; refresh();
    let attempted = false, added = false;
    try {
      const cartResponse = await fetch(config.root + 'cart.js', { headers: { Accept: 'application/json' }, cache: 'no-store' });
      if (!cartResponse.ok) throw new Error('Cart could not be checked');
      const currentCart = await cartResponse.json();
      if (!Array.isArray(currentCart.items)) throw new Error('Cart data is missing');
      inCart = cartQuantities(currentCart.items);
      selected = evaluateSelection(config, slots, inCart);
      if (!selected.valid) { message = issueMessage(selected); cartLink.hidden = false; return; }
      const drawer = document.getElementById('cart-drawer');
      const sectionId = drawer?.closest('.shopify-section')?.id.replace('shopify-section-', '');
      const payload = { items: selected.items };
      if (sectionId) { payload.sections = [sectionId]; payload.sections_url = location.pathname + location.search; }
      attempted = true;
      const response = await fetch(config.root + 'cart/add.js', { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(payload) });
      if (!response.ok) {
        needsCartReview = true; message = config.error; cartLink.hidden = false;
        document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
        return;
      }
      added = true;
      selected.items.forEach(item => inCart.set(item.id, (inCart.get(item.id) || 0) + item.quantity));
      const data = await response.json();
      if (!drawer || !sectionId || !data.sections?.[sectionId] || typeof drawer.show !== 'function') { location.assign(config.cartUrl); return; }
      const parsed = new DOMParser().parseFromString(data.sections[sectionId], 'text/html');
      const fresh = parsed.getElementById('cart-drawer');
      if (!fresh) { location.assign(config.cartUrl); return; }
      drawer.replaceChildren(...Array.from(fresh.childNodes));
      drawer.classList.toggle('drawer--center-body', fresh.classList.contains('drawer--center-body'));
      document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
      await drawer.show(); message = config.success; cartLink.hidden = false;
    } catch (error) {
      if (added) { location.assign(config.cartUrl); }
      else {
        needsCartReview = attempted;
        message = attempted ? config.uncertain : config.checkError;
        cartLink.hidden = !attempted;
      }
    } finally {
      busy = false; offers.disabled = false; refresh();
    }
  });
  profile.dataset.finishReady = 'true';
  offers.disabled = false;
  refresh();
}

if (typeof document !== 'undefined') {
  document.querySelectorAll('[data-nw-finish-purchase]').forEach(initFinishPurchase);
  document.addEventListener('shopify:section:load', event => event.target.querySelectorAll('[data-nw-finish-purchase]').forEach(initFinishPurchase));
}
